/**
 * Competition scoring — the whole of it, in one place, for the client AND the
 * Cloud Functions.
 *
 * Scoring systems:
 *   "ifsc_classic" — Classic IFSC: tops/zones count + attempt tiebreakers.
 *                    A top implies zone (1 zone, zoneAttempts = min(topAttempts, zoneAttempts)).
 *   "ifsc_2024"    — IFSC 2024 point-based: Top = 25 pts, Zone = 10 pts, −0.1 per attempt.
 *                    Only highest hold counts (top supersedes zone). Score per boulder ≥ 0.
 *   "tops_and_zones" — Simple: score = tops × 25 + zones × 5, tiebreak by fewer attempts.
 *   "points"       — Pool distribution: point pool ÷ toppers per route.
 *   "classic_redpoint" — Fixed-point routes, best N tops counted. Optional flash bonus.
 *
 * WHY ONE FILE. The server's `recomputeGroupRanking` writes `rankings/{groupId}`,
 * and every TV and public dashboard renders that document; the client scores the
 * same attempts live. They used to be two hand-kept copies, and a divergence put
 * one placing on the wall and another in the app — visible to spectators before
 * it was visible to anyone who could fix it. Now both load this module.
 *
 * `rankingComposer.test.js` names this file as the ONLY place a scorer may be
 * called from: everything else goes through `composeGroupRanking`.
 */
import { compareNames } from "./nameSort.js";

/**
 * Assign **standard competition ranks** ("1224 ranking") to a sorted
 * [uid, score] entry list. Items the comparator declares fully-tied
 * (returns 0 across all tiebreakers) share the same rank; the next distinct
 * item picks up the rank equal to its 1-based position. So three tied at 1st
 * read 1, 1, 1, 4, 5 — and two tied for gold are followed by bronze (3rd),
 * matching IFSC and Olympic convention.
 *
 * Optional `isExcluded(entry) -> bool` predicate flags rows that should NOT
 * count toward the official ranking but still appear in the list with their
 * "would-be" rank (their position as if everyone were included). Excluded
 * rows carry the would-be rank in `rank` plus `excluded: true`; the next
 * INCLUDED row gets the rank corresponding to its position among included
 * rows (so excluding the would-be 2nd place leaves 1, 2 (excluded), 2, 3 —
 * the visible official ranks read 1, 2, 3 with the ghost row tucked between).
 *
 * The challenge board (`challenge.js`) ranks with the same function.
 */
export function assignRanksByComparator(sortedEntries, comparator, isExcluded) {
  let lastIncludedRank = 0;
  let lastFullRank = 0;
  let includedSeen = 0;
  return sortedEntries.map((entry, i) => {
    const excluded = isExcluded ? !!isExcluded(entry) : false;
    // Full-list rank — used as the would-be rank for excluded rows ("(2)").
    // Tied with previous → same rank; otherwise → 1-based position in the
    // full sorted list, so a 1, 1, 3 sequence appears after a tied pair.
    const fullRank = (i > 0 && comparator(sortedEntries[i - 1], entry) === 0)
      ? lastFullRank
      : i + 1;
    lastFullRank = fullRank;
    if (excluded) {
      return { entry, rank: fullRank, excluded: true };
    }
    includedSeen += 1;
    // Included rank — compares against the previous INCLUDED row only,
    // skipping over excluded rows so they don't push other competitors down.
    let prevIncluded = null;
    for (let j = i - 1; j >= 0; j--) {
      if (!isExcluded || !isExcluded(sortedEntries[j])) {
        prevIncluded = sortedEntries[j];
        break;
      }
    }
    const rank = (prevIncluded && comparator(prevIncluded, entry) === 0)
      ? lastIncludedRank
      : includedSeen;
    lastIncludedRank = rank;
    return { entry, rank, excluded: false };
  });
}

/** Build an `isExcluded(entry)` predicate for a Set/array of uids — entry is
 *  a `[uid, score]` tuple from `Object.entries(scores)`. */
function buildExclusionPredicate(excludedUids) {
  if (!excludedUids) return null;
  const set = excludedUids instanceof Set ? excludedUids : new Set(excludedUids);
  if (set.size === 0) return null;
  return ([uid]) => set.has(uid);
}

/**
 * Calculate IFSC Classic ranking.
 * A top automatically counts as a zone (with zoneAttempts = topAttempts if lower).
 * Sort: tops desc → zones desc → topAttempts asc → zoneAttempts asc
 */
export function calculateIFSCClassicRanking(attempts, participantUids, excludedUids, hasZones = true) {
  // `hasZones === false` — see the note on `calculateRanking`. Nothing is
  // accumulated into `zones` / `zoneAttempts`, so both stay 0 for everyone and
  // the two zone terms of the comparator fall out on their own, leaving
  // tops → topAttempts.
  const zonesCount = hasZones !== false;
  const scores = {};
  for (const uid of participantUids) {
    scores[uid] = { tops: 0, zones: 0, topAttempts: 0, zoneAttempts: 0 };
  }
  for (const a of attempts) {
    if (!scores[a.uid]) continue;
    const s = scores[a.uid];
    if (a.topped) {
      s.tops++;
      s.topAttempts += a.topAttempts || 1;
      if (zonesCount) {
        // Top implies zone — count it even if not explicitly marked
        s.zones++;
        // Zone attempts = the earlier of zone or top attempts
        const impliedZoneAttempts = a.zoneReached
          ? Math.min(a.zoneAttempts || 1, a.topAttempts || 1)
          : (a.topAttempts || 1);
        s.zoneAttempts += impliedZoneAttempts;
      }
    } else if (zonesCount && a.zoneReached) {
      s.zones++;
      s.zoneAttempts += a.zoneAttempts || 1;
    }
  }
  const cmp = ([, a], [, b]) => {
    if (b.tops !== a.tops) return b.tops - a.tops;
    if (b.zones !== a.zones) return b.zones - a.zones;
    if (a.topAttempts !== b.topAttempts) return a.topAttempts - b.topAttempts;
    return a.zoneAttempts - b.zoneAttempts;
  };
  return assignRanksByComparator(Object.entries(scores).sort(cmp), cmp, buildExclusionPredicate(excludedUids))
    .map(({ entry: [uid, score], rank, excluded }) => ({ rank, uid, excluded, ...score }));
}

/**
 * Calculate IFSC 2024 point-based ranking.
 * Per boulder: Top = 25 pts, Zone = 10 pts (only highest counts).
 * Deduction: −0.1 per attempt (all falls, not just the successful ones).
 * Score per boulder = max(holdValue − (totalAttempts − 1) × 0.1, 0).
 * Total = sum across all boulders. Tiebreak: fewer total attempts.
 */
export function calculateIFSC2024Ranking(attempts, participantUids, excludedUids, hasZones = true) {
  const zonesCount = hasZones !== false;
  const scores = {};
  for (const uid of participantUids) {
    scores[uid] = { score: 0, tops: 0, zones: 0, topAttempts: 0, zoneAttempts: 0, totalAttempts: 0 };
  }
  for (const a of attempts) {
    if (!scores[a.uid]) continue;
    const s = scores[a.uid];
    // Determine highest hold reached and total attempts used
    let holdValue = 0;
    let totalAtt = 0;
    if (a.topped) {
      holdValue = 25;
      totalAtt = a.topAttempts || 1;
      s.tops++;
      s.topAttempts += totalAtt;
      if (zonesCount) {
        s.zones++; // top implies zone
        // When topped, zoneAttempts counts the attempts up to the zone (≤ topAttempts).
        s.zoneAttempts += Math.min(a.zoneAttempts || totalAtt, totalAtt);
      }
    } else if (zonesCount && a.zoneReached) {
      holdValue = 10;
      totalAtt = a.zoneAttempts || 1;
      s.zones++;
      s.zoneAttempts += totalAtt;
    } else {
      // No hold reached — each failed attempt still counts toward both totals.
      // Take the max so the tiebreaker counter (totalAttempts) reflects the
      // actual climb count on this route. Either field may be 0 if the
      // judging UI only tracked one of them. Without zones there is no second
      // field to reconcile: the top count is the whole truth.
      s.topAttempts += a.topAttempts || 0;
      if (zonesCount) s.zoneAttempts += a.zoneAttempts || 0;
      totalAtt = zonesCount
        ? Math.max(a.topAttempts || 0, a.zoneAttempts || 0)
        : (a.topAttempts || 0);
    }
    if (holdValue > 0) {
      // Deduct 0.1 per failed attempt (totalAtt − 1 = number of falls before success)
      const deductions = (totalAtt - 1) * 0.1;
      s.score += Math.max(holdValue - deductions, 0);
    }
    s.totalAttempts += totalAtt;
  }
  const cmp = ([, a], [, b]) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.totalAttempts - b.totalAttempts;
  };
  return assignRanksByComparator(Object.entries(scores).sort(cmp), cmp, buildExclusionPredicate(excludedUids))
    .map(({ entry: [uid, sc], rank, excluded }) => ({
      rank, uid, excluded,
      score: Math.round(sc.score * 10) / 10,
      tops: sc.tops, zones: sc.zones,
      topAttempts: sc.topAttempts, zoneAttempts: sc.zoneAttempts,
      totalAttempts: sc.totalAttempts,
    }));
}

/**
 * Calculate tops_and_zones ranking.
 * Score = tops * 25 + zones * 5. Tiebreaker: fewer total attempts.
 */
export function calculateTopsAndZonesRanking(attempts, participantUids, excludedUids, hasZones = true) {
  const zonesCount = hasZones !== false;
  const scores = {};
  for (const uid of participantUids) {
    scores[uid] = { tops: 0, zones: 0, topAttempts: 0, zoneAttempts: 0, totalAttempts: 0, score: 0 };
  }
  for (const a of attempts) {
    if (!scores[a.uid]) continue;
    const s = scores[a.uid];
    if (a.topped) s.tops++;
    if (zonesCount && a.zoneReached) s.zones++;
    s.topAttempts += a.topAttempts || 0;
    // Zone attempts stay out of BOTH the zone column and the attempt
    // tie-break: half a stale mirrored count is worse than none.
    if (zonesCount) {
      s.zoneAttempts += a.zoneAttempts || 0;
      s.totalAttempts += (a.topAttempts || 0) + (a.zoneAttempts || 0);
    } else {
      s.totalAttempts += a.topAttempts || 0;
    }
  }
  for (const s of Object.values(scores)) {
    s.score = s.tops * 25 + s.zones * 5;
  }
  const cmp = ([, a], [, b]) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.totalAttempts - b.totalAttempts;
  };
  return assignRanksByComparator(Object.entries(scores).sort(cmp), cmp, buildExclusionPredicate(excludedUids))
    .map(({ entry: [uid, score], rank, excluded }) => ({ rank, uid, excluded, ...score }));
}

/**
 * Calculate points-based ranking (pool distribution).
 * Each route has a point pool; divided among all who topped it.
 */
export function calculatePointsRanking(attempts, participantUids, pointPool, excludedUids) {
  // Only a participant's top splits the pool. The other four scorers ignore a
  // non-participant's attempt by construction (they only score seeded uids);
  // this one also COUNTS toppers, so without the guard a stray attempt would
  // shrink everyone's share whenever the caller skipped `calculateRanking`'s
  // pre-filter.
  const participants = new Set(participantUids);
  const toppersPerRoute = {};
  for (const a of attempts) {
    if (a.topped && participants.has(a.uid)) {
      toppersPerRoute[a.routeId] = (toppersPerRoute[a.routeId] || 0) + 1;
    }
  }
  const scores = {};
  for (const uid of participantUids) {
    scores[uid] = { points: 0, totalAttempts: 0 };
  }
  for (const a of attempts) {
    if (!scores[a.uid]) continue;
    scores[a.uid].totalAttempts += (a.topAttempts || 0);
    if (a.topped && toppersPerRoute[a.routeId]) {
      scores[a.uid].points += pointPool / toppersPerRoute[a.routeId];
    }
  }
  const cmp = ([, a], [, b]) => {
    if (b.points !== a.points) return b.points - a.points;
    return a.totalAttempts - b.totalAttempts;
  };
  return assignRanksByComparator(Object.entries(scores).sort(cmp), cmp, buildExclusionPredicate(excludedUids))
    .map(({ entry: [uid, score], rank, excluded }) => ({ rank, uid, excluded, points: Math.round(score.points * 10) / 10, totalAttempts: score.totalAttempts }));
}

/**
 * Calculate Classic Redpoint ranking.
 * Fixed-point routes, best N tops counted. Optional flash bonus.
 * Tiebreak: total points desc → attempts asc → all tops count desc.
 * A route the organizer gave no value scores 0 — there is deliberately no
 * fallback to the gym's own difficulty points (see the client's
 * getCompetitionRoutePoints for why).
 * @param {object} opts - { routeCompPoints, countBest, flashBonus, excludedUids }
 */
export function calculateClassicRedpointRanking(attempts, participantUids, opts = {}) {
  const { routeCompPoints = {}, countBest = 10, flashBonus = null, excludedUids = null } = opts;

  const scores = {};
  for (const uid of participantUids) {
    scores[uid] = { tops: [], totalPoints: 0, totalAttempts: 0, topsCount: 0, allTopsCount: 0, flashCount: 0, allFlashCount: 0 };
  }

  for (const a of attempts) {
    if (!scores[a.uid] || !a.topped) continue;
    const basePoints = routeCompPoints[a.routeId] ?? 0;
    const isFlash = a.topAttempts === 1;
    const bonus = (isFlash && flashBonus) ? flashBonus : 0;
    scores[a.uid].tops.push({
      routeId: a.routeId,
      earnedPoints: basePoints + bonus,
      basePoints,
      topAttempts: a.topAttempts || 1,
      isFlash,
    });
  }

  for (const s of Object.values(scores)) {
    s.tops.sort((a, b) => b.earnedPoints - a.earnedPoints);
    s.allTopsCount = s.tops.length;
    s.allFlashCount = s.tops.filter(t => t.isFlash).length;
    const best = s.tops.slice(0, countBest);
    s.totalPoints = best.reduce((sum, t) => sum + t.earnedPoints, 0);
    s.totalAttempts = best.reduce((sum, t) => sum + t.topAttempts, 0);
    s.topsCount = best.length;
    s.flashCount = best.filter(t => t.isFlash).length;
    delete s.tops; // clean up array — not needed in result
  }

  const cmp = ([, a], [, b]) => {
    if (b.totalPoints !== a.totalPoints) return b.totalPoints - a.totalPoints;
    if (a.totalAttempts !== b.totalAttempts) return a.totalAttempts - b.totalAttempts;
    return b.allTopsCount - a.allTopsCount;
  };
  return assignRanksByComparator(Object.entries(scores).sort(cmp), cmp, buildExclusionPredicate(excludedUids))
    .map(({ entry: [uid, score], rank, excluded }) => ({ rank, uid, excluded, ...score }));
}

/**
 * Calculate ranking based on the competition's scoring system.
 * Backward-compatible: old "ifsc" key maps to "ifsc_classic".
 *
 * `hasZones === false` (the organizer turned zones off; forced off for the
 * points and classic_redpoint systems) means the zone fields take NO part in
 * the result — not the zone column, not the score, not the tie-breaks. This
 * argument used to be accepted and then ignored, so a zones-off competition
 * was still ranked on invisible zone data. And that data is not even written
 * consistently: the JAM scorecard leaves the fields alone while the judge
 * Cloud Function mirrors a top onto them, so whether a row carried a zone came
 * down to who recorded it. Old rows may still carry the fields — ignoring them
 * here is what makes that harmless.
 * @param {object} [opts] - { routeCompPoints, countBest, flashBonus, excludedUids }
 *   `excludedUids` (Set or array): competitors to render with their would-be
 *   rank but mark `excluded: true` and skip when assigning official ranks.
 */
export function calculateRanking(attempts, participantUids, scoringSystem, pointPool, hasZones, opts) {
  const filtered = attempts.filter(a => participantUids.includes(a.uid));
  const excludedUids = opts?.excludedUids || null;
  switch (scoringSystem) {
    case "ifsc_classic":
    case "ifsc": // backward compat for existing competitions
      return calculateIFSCClassicRanking(filtered, participantUids, excludedUids, hasZones);
    case "ifsc_2024":
      return calculateIFSC2024Ranking(filtered, participantUids, excludedUids, hasZones);
    case "points":
      return calculatePointsRanking(filtered, participantUids, pointPool || 1000, excludedUids);
    case "classic_redpoint":
      return calculateClassicRedpointRanking(filtered, participantUids, { ...(opts || {}), excludedUids });
    case "tops_and_zones":
    default:
      return calculateTopsAndZonesRanking(filtered, participantUids, excludedUids, hasZones);
  }
}

/* ── Entry-fee gating and per-group route points ───────────────────────── */

/**
 * Build a Set of UIDs that are considered "paid".
 * - Non-guest competitors: check their own entryFeePaid flag.
 * - Proxy guests (isGuest + proxyUid): check their parent's entryFeePaid flag.
 * - Admin-registered guests (isGuest, no proxyUid): check their own entryFeePaid flag.
 *
 * Null-tolerant on purpose: the competition-climbs sync hands it whatever the
 * registrations query returned, and a holey array must not throw there.
 */
export function buildPaidUidSet(registrations) {
  // Index potential parent registrations by uid for fast lookup.
  // Parents can be active competitors or role-less stubs — a guest-only payer
  // has no registration of their own, so the guest flow writes a stub to carry
  // the payment fields (the invoice identity itself lives on their contact doc).
  const parentMap = {};
  (registrations || []).forEach((r) => {
    if (r && !r.isGuest) parentMap[r.uid] = r;
  });
  const paidUids = new Set();
  (registrations || []).forEach((r) => {
    if (!r || r.role !== "competitor") return;
    if (r.isGuest && r.proxyUid) {
      // Proxy guest: paid if parent is paid
      const parent = parentMap[r.proxyUid];
      if (parent && parent.entryFeePaid === true) paidUids.add(r.uid);
    } else if (r.entryFeePaid === true) {
      // Own registration or admin-registered guest: check own flag
      paidUids.add(r.uid);
    }
  });
  return paidUids;
}

/**
 * Filter attempts to exclude unpaid competitors when entry fee is enabled.
 * Used by ranking calculations and close snapshot.
 */
export function filterPaidCompetitors(attempts, registrations, entryFeeEnabled) {
  if (!entryFeeEnabled) return attempts;
  const paidUids = buildPaidUidSet(registrations);
  return attempts.filter(a => paidUids.has(a.uid));
}

/**
 * Filter participant UIDs to exclude unpaid competitors when entry fee is enabled.
 */
export function filterPaidUids(uids, registrations, entryFeeEnabled) {
  if (!entryFeeEnabled) return uids;
  const paidUids = buildPaidUidSet(registrations);
  return uids.filter(uid => paidUids.has(uid));
}

/**
 * Resolve routeCompPoints for a specific group. Falls back to global routeCompPoints.
 */
export function getGroupRouteCompPoints(settings, groupId) {
  if (groupId && settings?.groupRouteCompPoints?.[groupId] && Object.keys(settings.groupRouteCompPoints[groupId]).length > 0) {
    return settings.groupRouteCompPoints[groupId];
  }
  return settings?.routeCompPoints || {};
}

/**
 * Assemble ONE group's ranking rows: the fee gate, the scorer, and the
 * denormalized name/start-number, in that order.
 *
 * WHY THIS IS A FUNCTION AND NOT A PROCEDURE WRITTEN TWICE
 *
 * The parts used to be shared; the ASSEMBLY was not. The client wrote it
 * inline in `computeGroupRankings`, the server inline in
 * `recomputeGroupRanking`, and a step added to one and forgotten in the other
 * showed up as the TV wall disagreeing with the phone — with both boards
 * rendering, and both looking right.
 *
 * WHAT STAYS AT THE CALL SITE, ON PURPOSE
 *
 * Who is being ranked, and from which registrations the fee is read, genuinely
 * differ and must not be hidden in here:
 *
 *   - the client holds every registration in memory, so `payerRegs` is simply
 *     all of them;
 *   - the server reads ONE group (that scoping is what keeps a 250-competitor
 *     competition affordable at one recompute per attempt write), so it has to
 *     re-fetch the payers its guests point at first;
 *   - a virtual finals group ranks `finalistUids` over attempts sliced by
 *     finals session, and resolves names against the SOURCE group.
 *
 * Passing those in keeps this function honest about what it does not know.
 *
 * @param {object}   settings            competition.settings
 * @param {string}   routePointsGroupId  group whose per-group route points apply
 *                                       (the SOURCE group for a finals view)
 * @param {string[]} competitorUids      who is ranked
 * @param {object[]} attempts            already scoped to those competitors' round
 * @param {object[]} payerRegs           registrations the entry-fee gate may read
 * @param {object[]} nameRegs            registrations that resolve name + start number
 * @param {Set<string>} excludedUids     `excludeFromRanking` ghost rows
 */
export function composeGroupRanking({
  settings,
  routePointsGroupId,
  competitorUids,
  attempts,
  payerRegs,
  nameRegs,
  excludedUids,
}) {
  const entryFeeEnabled = settings?.entryFee?.enabled === true;
  const regByUid = new Map((nameRegs || []).map((r) => [r.uid, r]));

  // A perfect tie — every comparator key equal — is broken by NAME.
  //
  // The scorers seed their score map by walking `participantUids`, and
  // `Array.prototype.sort` is stable, so whatever order arrives here survives
  // into the tied block. That used to be the order the registrations happened
  // to be read in, which is a rule nobody wrote down and nobody could rely on;
  // ordering the input by name turns it into one. `uid` breaks a tie between
  // two identical names, so the result is total.
  const nameOf = (uid) => {
    const n = regByUid.get(uid)?.displayName;
    return typeof n === "string" && n.trim() ? n : null;
  };
  const orderedUids = [...competitorUids].sort((a, b) => {
    const na = nameOf(a);
    const nb = nameOf(b);
    // A row with no resolvable registration renders as "?". Collating that as
    // an empty string would float every one of them to the TOP of a tied
    // block, ahead of named competitors — so they sort last instead.
    if ((na === null) !== (nb === null)) return na === null ? 1 : -1;
    if (na !== null) {
      const byName = compareNames(na, nb);
      if (byName !== 0) return byName;
    }
    return a < b ? -1 : a > b ? 1 : 0;
  });

  const paidUids = filterPaidUids(orderedUids, payerRegs, entryFeeEnabled);
  const paidAttempts = filterPaidCompetitors(attempts, payerRegs, entryFeeEnabled);

  const rows = calculateRanking(
    paidAttempts,
    paidUids,
    settings?.scoringSystem || "tops_and_zones",
    settings?.pointPool || 1000,
    settings?.hasZones !== false,
    {
      countBest: settings?.countBest || 10,
      flashBonus: settings?.flashBonus || null,
      routeCompPoints: getGroupRouteCompPoints(settings, routePointsGroupId),
      excludedUids,
    },
  );

  // Resolved here so the server's precomputed document is a COMPLETE rendering
  // source — a TV reads that one doc and nothing else.
  return rows.map((r) => {
    const reg = regByUid.get(r.uid);
    return { ...r, displayName: reg?.displayName || "?", startNumber: reg?.startNumber || null };
  });
}

/**
 * Single source of truth for computing a group's ranking on the public
 * dashboards (CompetitionDashboard + CompetitionTvDashboard). Returns a
 * ranking array with displayName + startNumber resolved from registrations.
 *
 *   activeGroup     — entry from buildDisplayGroups() (regular or virtual finals)
 *   competition     — competition doc
 *   registrations   — full registrations array
 *   attempts        — full attempts array (live reads)
 *   closedResults   — map { [groupId]: rankings[] } loaded from the /results
 *                     subcollection when the competition is closed; a hit here
 *                     short-circuits the live calculation.
 *
 * The emulator `rankingComposition.test.js` runs this against the server's
 * precomputed `rankings/{groupId}` document from the same data — the two
 * CALLERS feed the composer differently, and that is what it pins.
 */
export function computeGroupRankings(activeGroup, competition, registrations, attempts, closedResults = {}) {
  if (!activeGroup || !competition) return [];
  const resultKey = activeGroup.id;
  if (competition.status === "closed" && closedResults[resultKey]) {
    return closedResults[resultKey];
  }

  const settings = competition.settings;

  // Build a Set of excluded uids — registrations carrying
  // `excludeFromRanking: true` are scored normally, but the calculator marks
  // them excluded so the dashboard renders the would-be-rank ghost row.
  const excludedUids = new Set(
    (registrations || [])
      .filter((r) => r.role === "competitor" && r.excludeFromRanking === true)
      .map((r) => r.uid),
  );

  if (activeGroup.isFinals) {
    // A virtual group: the finalists are named on the group, their attempts are
    // the SOURCE group's sliced by finals session, and their names resolve
    // against that source group — three reasons this cannot just pass `id`.
    const finalsSessionIdSet = new Set(activeGroup.finalsSessionIds || []);
    return composeGroupRanking({
      settings,
      routePointsGroupId: activeGroup.sourceGroupId,
      competitorUids: activeGroup.finalistUids || [],
      attempts: attempts.filter(
        (a) => a.groupId === activeGroup.sourceGroupId && finalsSessionIdSet.has(a.sessionId),
      ),
      payerRegs: registrations,
      nameRegs: registrations.filter((r) => r.groupId === activeGroup.sourceGroupId),
      excludedUids,
    });
  }

  const groupRegs = registrations.filter((r) => r.groupId === activeGroup.id && r.role === "competitor");
  return composeGroupRanking({
    settings,
    routePointsGroupId: activeGroup.id,
    competitorUids: groupRegs.map((r) => r.uid),
    attempts: attempts.filter((a) => a.groupId === activeGroup.id),
    // Every registration, because a proxy guest's fee is paid by their parent,
    // who may sit in another group entirely.
    payerRegs: registrations,
    nameRegs: groupRegs,
    excludedUids,
  });
}
